import { useEffect, useState } from "react";
import { Page, Skeleton } from "../components/ui.jsx";
import { buildDoc, deliver } from "../lib/docPdf.js";

// The resource library.
//
// Everything here comes from /api/docs, never from a bundled data module. That
// is the whole point of the endpoint: the tier gate is enforced where the text
// lives rather than on the card that links to it, so a member cannot read a
// senior document by opening the JavaScript bundle.
//
// The list arrives with metadata only. A body is fetched when a document is
// opened, and the server re-checks the tier on that request too.

export default function Docs({ back, level }) {
  const [rows, setRows] = useState(null);
  const [locked, setLocked] = useState(0);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(null);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    fetch("/api/docs")
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Could not load the library.");
        setRows(d.docs || []);
        setLocked(d.locked || 0);
      })
      .catch((e) => setError(String(e.message || e)));
  }, []);

  if (error) {
    return (
      <Page back={back} eyebrow="Library" title="Resources.">
        <div className="card locked">
          <div className="name">
            <span>🔒 {error}</span>
          </div>
        </div>
      </Page>
    );
  }

  if (open) return <Reader id={open} back={() => setOpen(null)} />;

  const cats = ["all", ...new Set((rows || []).map((d) => d.category))];
  const shown = (rows || []).filter((d) => category === "all" || d.category === category);

  return (
    <Page
      back={back}
      eyebrow="Library"
      title="Resources."
      intro="Written here, for this house. Read one on your phone, or save it to keep."
    >
      {!rows ? (
        <Skeleton />
      ) : rows.length === 0 ? (
        <div className="empty-note">
          Nothing at your access level yet. This library fills as documents are
          written — it does not backfill anything from before it existed.
        </div>
      ) : (
        <>
          {cats.length > 2 && (
            <div className="chips">
              {cats.map((c) => (
                <button
                  key={c}
                  className={"chip" + (category === c ? " on" : "")}
                  onClick={() => setCategory(c)}
                >
                  {c === "all" ? "All" : c}
                </button>
              ))}
            </div>
          )}

          {shown.map((d) => (
            <button key={d.id} className="card doc-card" onClick={() => setOpen(d.id)}>
              <div className="name">
                <span>{d.title}</span>
                <span className="arrow">→</span>
              </div>
              <div className="desc">{d.summary}</div>
              <div className="doc-meta">
                {d.category} · {d.minutes} min read
                {d.updated ? " · updated " + d.updated : ""}
              </div>
            </button>
          ))}

          {/* A count, never a title. Knowing something exists above your tier
              is not a leak; naming it would be. */}
          {locked > 0 && (
            <div className="empty-note">
              {locked} more {locked === 1 ? "document is" : "documents are"} held above{" "}
              {level ? "your" : "this"} access level.
            </div>
          )}
        </>
      )}
    </Page>
  );
}

/** One document, plus the control that turns it into a file to keep. */
function Reader({ id, back }) {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    fetch("/api/docs/" + encodeURIComponent(id))
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Could not open that document.");
        setDoc(d.doc);
      })
      .catch((e) => setError(String(e.message || e)));
  }, [id]);

  const save = async () => {
    if (saving || !doc) return;
    setSaving(true);
    setNote("");
    try {
      const pdf = await buildDoc(doc);
      const outcome = await deliver(pdf, `${doc.title.replace(/[^\w]+/g, "-")}.pdf`);
      if (outcome === "saved") setNote("Saved. Check your downloads.");
      else if (outcome === "shared") setNote("Sent to where you chose.");
    } catch {
      setNote("Could not build the file just now. Try again in a moment.");
    }
    setSaving(false);
    setTimeout(() => setNote(""), 3200);
  };

  if (error) return <div className="empty-note">{error}</div>;
  if (!doc) return <Skeleton />;

  return (
    <Page
      back={back}
      eyebrow={`${doc.category} · ${doc.minutes} min read`}
      title={doc.title}
      keepCase
      intro={doc.summary}
    >
      <div className="chips noprint" style={{ marginBottom: 20 }}>
        <button className="chip" onClick={save} disabled={saving}>
          {saving ? "Building…" : "⬇ Save as PDF"}
        </button>
        <button className="chip" onClick={() => window.print()}>
          🖨 Print
        </button>
      </div>
      {note && <div className="empty-note">{note}</div>}

      <article className="doc-body">
        {doc.blocks.map((b, i) => {
          if (b.type === "h") return <h3 key={i}>{b.text}</h3>;
          if (b.type === "rule") return <hr key={i} />;
          if (b.type === "ul")
            return (
              <ul key={i}>
                {b.items.map((it, n) => (
                  <li key={n}>{it}</li>
                ))}
              </ul>
            );
          return <p key={i}>{b.text}</p>;
        })}
      </article>

      <div className="privacy-note">
        {doc.author} · {doc.updated || "—"} · visible at {doc.minLevel} and above.
        Access is checked on the server, so this text is never in the app's public
        bundle.
      </div>
    </Page>
  );
}
